// Localização: frontend/src/components/Navigation/MobileNav.jsx (Refatorado - V12 - Logout no Menu Secundário)

import React, { useState } from 'react'; 
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext'; 
import { usePlanAccess } from '../../hooks/usePlanAccess';

// Importações Lucide necessárias
import { 
    LayoutDashboard, 
    CalendarCheck, 
    ClipboardList, 
    NotebookPen, 
    Users, 
    UsersRound, 
    MapPinCheck, 
    Pill, 
    PackageSearch, 
    Settings2, 
    LogOut, // 💡 NOVO: Importando o ícone LogOut
    ChartCandlestick, // Ícone para Gestão Financeira
    Sparkles, // Ícone para Assistente de IA
    CreditCard, // Ícone para Gerenciar Assinaturas (Admin)
} from 'lucide-react';


// --------------------------------------------------------------------------------
// 1. MAPEAMENTO DE ÍCONES (Miniatura)
// --------------------------------------------------------------------------------
const iconMap = {
    'dashboard': LayoutDashboard,
    'agendamentos': CalendarCheck,
    'tarefas': ClipboardList, 
    'diario': NotebookPen, 
    'pacientes': Users,
    'gestao_pessoal': UsersRound, 
    'ponto': MapPinCheck, 
    'medicamentos': Pill, 
    'equipamentos': PackageSearch, 
    'ferramentas': Settings2,
    'configuracoes': Settings2,
    'profile': UsersRound, 
    'sair': LogOut,
    'financeiro': ChartCandlestick, // Ícone para Gestão Financeira
    'assistente_ia': Sparkles, // Ícone para Assistente de IA
    'admin_assinaturas': CreditCard, // Gerenciar Assinaturas (Admin)
};


// --------------------------------------------------------------------------------
// 2. DEFINIÇÃO DOS ITENS DE NAVEGAÇÃO MOBILE COM RBAC E PLANO
// --------------------------------------------------------------------------------
// 🛑 CORREÇÃO: Função para verificar acesso da mesma forma que PrivateRoute
const hasResourceAccess = (user, resource, checkPermission, isModuleAvailable, subscription) => {
    // Se usuário não está vinculado ou está pendente de aceitação, não tem acesso
    if (!user?.organizacao_id || user?.status === 'pendente_aceitacao' || user?.status === 'pendente_vinculacao') {
        return false;
    }

    // Roles de alto privilégio têm acesso total
    const isHighPrivilege = user?.role === 'proprietario' || user?.role === 'admin';
    if (isHighPrivilege) {
        // Verificar apenas se módulo está disponível no plano
        return isModuleAvailable(resource);
    }

    // Verificar permissões RBAC
    if (!checkPermission(resource, 'can_access')) {
        return false;
    }

    // Verificar se módulo está disponível no plano
    if (!isModuleAvailable(resource)) {
        return false;
    }

    return true;
};

// 🛑 CORREÇÃO: Função para filtrar navegação baseada em permissões reais E plano
// Sempre mantém 5 itens no menu principal, com Dashboard no centro (posição 2)
const getMobileNavigation = (user, checkPermission, isModuleAvailable, subscription) => {
    // Item de Sair com um path fictício para ser capturado como um botão
    const logoutItem = { path: '/logout-action', icon: 'sair', label: 'Sair', resource: null };
    
    // Lista completa de todos os itens possíveis (em ordem de prioridade)
    // Ordem de prioridade: itens mais usados primeiro
    const allAvailableItems = [
        { path: '/ponto', icon: 'ponto', label: 'Check-in', resource: 'check_in_out' },
        { path: '/pacientes', icon: 'pacientes', label: 'Pacientes', resource: 'pacientes' },
        { path: '/agendamentos', icon: 'agendamentos', label: 'Agenda', resource: 'agendamentos' },
        { path: '/diario', icon: 'diario', label: 'Diário', resource: 'diario' },
        { path: '/tarefas', icon: 'tarefas', label: 'Tarefas', resource: 'agenda_tarefas' },
        { path: '/medicamentos', icon: 'medicamentos', label: 'Medicamentos', resource: 'medicamentos' },
        { path: '/equipamentos', icon: 'equipamentos', label: 'Equipamentos', resource: 'equipamentos' },
        { path: '/usuarios', icon: 'gestao_pessoal', label: 'Gestão', resource: 'usuarios' },
        { path: '/config/permissoes', icon: 'configuracoes', label: 'Permissões', resource: 'config' },
        { path: '/financeiro/dashboard', icon: 'financeiro', label: 'Financeiro', resource: 'financeiro' },
        { path: '/assistente-ia', icon: 'assistente_ia', label: 'IA', resource: 'ia' },
    ];
    
    // ✅ NOVO: Adicionar item de Admin Assinaturas apenas para admins globais
    const adminRoles = ['global_admin', 'super_admin', 'admin'];
    if (user && adminRoles.includes(user.role)) {
        allAvailableItems.push({
            path: '/admin/assinaturas',
            icon: 'admin_assinaturas',
            label: 'Assinaturas',
            resource: null, // Não precisa de verificação de recurso, apenas role
            isAdminOnly: true
        });
    }
    
    // Dashboard item (será posicionado no centro)
    const dashboardItem = { path: '/dashboard', icon: 'dashboard', label: 'Dash', resource: 'dashboard' };
    
    // ✅ CORREÇÃO: Filtrar itens usando a mesma lógica do PrivateRoute
    const filteredItems = allAvailableItems.filter(item => {
        // Se é item admin-only, verificar apenas role
        if (item.isAdminOnly) {
            return adminRoles.includes(user?.role);
        }
        return hasResourceAccess(user, item.resource, checkPermission, isModuleAvailable, subscription);
    });
    
    // Verificar se Dashboard tem acesso usando a mesma lógica
    const hasDashboardAccess = hasResourceAccess(user, 'dashboard', checkPermission, isModuleAvailable, subscription);
    
    // Construir menu principal com exatamente 5 itens, Dashboard no centro
    const mainItems = [];
    const secondaryItems = [];
    
    if (hasDashboardAccess) {
        // Dashboard vai no centro (posição 2 de 0-4)
        // Estrutura: [item1, item2, Dashboard, item3, item4]
        
        // Pegar 2 itens para antes do Dashboard
        const beforeDashboard = filteredItems.slice(0, 2);
        
        // Pegar 2 itens para depois do Dashboard
        const afterDashboard = filteredItems.slice(2, 4);
        
        // Construir menu principal: [item1, item2, Dashboard, item3, item4]
        mainItems.push(...beforeDashboard);
        mainItems.push(dashboardItem);
        mainItems.push(...afterDashboard);
        
        // Itens restantes (após os 4 primeiros) vão para o secundário
        secondaryItems.push(...filteredItems.slice(4));
    } else {
        // Se não tem Dashboard, usar os 5 primeiros itens
        mainItems.push(...filteredItems.slice(0, 5));
        secondaryItems.push(...filteredItems.slice(5));
    }
    
    // Se temos mais de 5 itens, mover extras para secundário
    if (mainItems.length > 5) {
        const extras = mainItems.splice(5);
        secondaryItems.unshift(...extras);
    }
    
    // Adicionar logout ao final do secundário
    secondaryItems.push(logoutItem);
    
    // Adicionar botão "Ferramentas" se houver itens no secundário (além do logout)
    const hasSecondaryItems = secondaryItems.some(item => item.path !== '/logout-action');
    if (hasSecondaryItems) {
        // Se já temos 5 itens, substituir o último por "Ferramentas"
        if (mainItems.length === 5) {
            const lastItem = mainItems[4];
            // Se o último item não é Dashboard, mover para secundário
            if (lastItem && lastItem.path !== '/dashboard' && lastItem.resource) {
                secondaryItems.unshift(lastItem);
            }
            mainItems[4] = { path: '#ferramentas', icon: 'ferramentas', label: 'Ferramentas', resource: null };
        } else if (mainItems.length < 5) {
            // Se temos menos de 5, adicionar "Ferramentas" para completar até 5
            mainItems.push({ path: '#ferramentas', icon: 'ferramentas', label: 'Ferramentas', resource: null });
        }
    }
    
    // Garantir que sempre temos exatamente 5 itens (ou menos se não houver permissões suficientes)
    return {
        main: mainItems.slice(0, 5),
        secondary: secondaryItems
    };
};


// --------------------------------------------------------------------------------
// 3. COMPONENTE MOBILE NAV
// --------------------------------------------------------------------------------

const MobileNav = () => {
    const { user, logout, handleLogout, subscription } = useAuth(); 
    const { checkPermission } = usePermissions();
    const { isModuleAvailable } = usePlanAccess(); // NOVO: Verificação de plano
    const handleLogoutAction = logout || handleLogout; 
    
    const location = useLocation(); 
    const [isMenuOpen, setIsMenuOpen] = useState(false); 

    // 🛑 CORREÇÃO: Usar permissões reais E verificação de plano (mesma lógica do PrivateRoute)
    const { main: mainNavigation, secondary: secondaryNavigation } = getMobileNavigation(
        user,
        checkPermission,
        isModuleAvailable,
        subscription
    );

    if (!user) return null; 
    
    // Verificar se há itens no menu secundário além do logout
    const hasSecondaryItems = secondaryNavigation.some(item => item.path !== '/logout-action');
    
    // Funções de Renderização 
    
    const renderIcon = (Icon, isActive) => (
        <div 
            className={`
                flex items-center justify-center h-9 w-9 rounded-full mb-0.5 transition-colors duration-200
                ${isActive 
                    ? 'bg-cor-fundo-circulo-menu-mobile-quando-selecionado' 
                    : 'text-white' 
                }
            `}
        >
            <Icon 
                size={22} 
                className={`${isActive ? 'text-cor-icone-menu-mobile-quando-selecionado' : 'text-white'}`}
            />
        </div>
    );

    // 💡 NOVO: Função para renderizar o Botão de Logout no Menu Secundário
    const renderLogoutButton = (item) => {
        const IconComponent = iconMap[item.icon];
        
        return (
            <button
                key={item.path}
                onClick={() => {
                    setIsMenuOpen(false); // Fecha o menu secundário
                    handleLogoutAction && handleLogoutAction(); // Chama a função de logout
                }}
                // Estilos para o botão de Logout dentro do menu secundário
                className={`flex items-center p-2 rounded-lg text-sm font-medium transition-colors w-full
                            bg-red-700/50 hover:bg-red-600/70 text-white font-bold
                `}
                title={item.label}
            >
                <IconComponent 
                    size={20} 
                    className={`flex-shrink-0 mr-3 text-white`} 
                />
                <span className="flex-grow">{item.label}</span>
            </button>
        );
    };

    // 💡 renderNavLink (Mantido e ajustado para não ter Logout no main)
    const renderNavLink = (item) => {
        const IconComponent = iconMap[item.icon];
        // 🛑 PREVENÇÃO: Se o item de logout cair no menu principal por algum erro, não o renderizamos aqui como NavLink
        if (item.path === '/logout-action') return null; 
        
        const isActive = location.pathname === item.path || (location.pathname.startsWith(item.path + '/') && item.path !== '/');
        
        const activeClasses = 'text-white'; 
        const inactiveClasses = 'text-white/80'; 
        
        // Caso 'Ferramentas' (botão de toggle) - só aparece se houver itens no menu secundário
        if (item.path === '#ferramentas' && hasSecondaryItems) {
             const FerramentasIcon = iconMap['ferramentas'];
             const FerramentasClasses = isMenuOpen ? 'text-white' : 'text-white/80'; 

             return (
                 <button
                     key={item.path}
                     onClick={() => setIsMenuOpen(!isMenuOpen)}
                     className={`
                         flex flex-col items-center p-1.5 rounded-lg text-sm font-medium 
                         transition-colors duration-200 w-full max-w-[100px]
                         ${FerramentasClasses}
                     `}
                     title={item.label}
                 >
                     {renderIcon(FerramentasIcon, isMenuOpen)}
                     <span className="truncate">
                         {item.label}
                     </span>
                 </button>
             );
        }

        return (
            <NavLink
                key={item.path}
                to={item.path}
                onClick={() => isMenuOpen && setIsMenuOpen(false)} 
                className={`
                    flex flex-col items-center p-1.5 rounded-lg text-sm font-medium 
                    transition-colors duration-200 w-full max-w-[100px]
                    ${isActive ? activeClasses : inactiveClasses}
                `}
                title={item.label}
            >
                {IconComponent && renderIcon(IconComponent, isActive)}
                <span className="truncate">
                    {item.label}
                </span>
            </NavLink>
        );
    };

    return (
        <div className="fixed w-full bottom-0 z-50 lg:hidden p-4"> 
            
            {/* Menu Flutuante (Ferramentas) */}
            {hasSecondaryItems && isMenuOpen && (
                 <div 
                    className="absolute bottom-[112px] right-4 z-[70]
                                flex flex-col items-end space-y-1 p-3 w-4/5 max-w-[250px] 
                                rounded-2xl shadow-xl 
                                bg-gradient-to-r from-cor-fundo-menu-mobile-degrad to-cor-fundo-menu-mobile-degrad-2"
                >
                    {/* Título do menu secundário */}
                    <div className="text-sm font-bold text-white/90 border-b border-white/30 pb-2 mb-1 w-full text-center">
                        Ferramentas de Gestão
                    </div>

                    <div className="flex flex-col gap-1 w-full"> 
                        {secondaryNavigation.map((item) => {
                            
                            // 🛑 MUDANÇA: Chama o botão de Logout se for o item correto
                            if (item.path === '/logout-action') {
                                return renderLogoutButton(item);
                            }
                            
                            // Renderiza NavLink para itens normais do menu secundário
                            const IconComponent = iconMap[item.icon];
                            const isActive = location.pathname === item.path || (location.pathname.startsWith(item.path + '/') && item.path !== '/');
                            
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`flex items-center p-2 rounded-lg text-sm font-medium transition-colors w-full
                                                ${isActive 
                                                    ? 'bg-cor-fundo-circulo-menu-mobile-quando-selecionado text-cor-icone-menu-mobile-quando-selecionado font-bold' 
                                                    : 'text-white' 
                                                }
                                    `}
                                >
                                    <IconComponent 
                                        size={20} 
                                        className={`flex-shrink-0 mr-3 
                                                     ${isActive ? 'text-cor-icone-menu-mobile-quando-selecionado' : 'text-white'}`
                                                } 
                                    />
                                    <span className="flex-grow">{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            )}


            {/* 1. Barra de Navegação Inferior (Flutuante e Arredondada) */}
            <nav 
                className="w-full mx-auto max-w-lg relative z-50" 
            >
                <div 
                    className="flex justify-around p-3 rounded-3xl shadow-[0_-5px_15px_rgba(0,0,0,0.2)] 
                                bg-gradient-to-r from-cor-fundo-menu-mobile-degrad to-cor-fundo-menu-mobile-degrad-2 relative z-50" 
                >
                    {mainNavigation.map((item) => renderNavLink(item))}
                </div>
            </nav>
        </div>
    );
};

export default MobileNav;